import type { DocumentStatus } from './document-status';

// Filtros de la Lista Maestra: tipos y traducción pura de la vista a los parámetros del
// API (sin router, testeable). El hook `use-url-filters` los sincroniza con la URL.

export type StatusSegment =
  | 'todos'
  | 'vigente'
  | 'en_revision'
  | 'por_firmar'
  // G2-8: Por firmar acotado a lo que le toca firmar al lector.
  | 'mis_firmas'
  // G4: con la etapa de capacitación abierta.
  | 'en_capacitacion'
  | 'vencidos'
  | 'obsoleto'
  | 'anulado';

const SEGMENTS: StatusSegment[] = [
  'todos',
  'vigente',
  'en_revision',
  'por_firmar',
  'mis_firmas',
  'en_capacitacion',
  'vencidos',
  'obsoleto',
  'anulado',
];

export interface UiFilters {
  segment: StatusSegment;
  q: string;
  norm: string | null;
  /** Cláusula de la norma (auditoría por requisito); solo con norma. */
  clause: string | null;
  /** Id de tipo de documento. */
  type: string | null;
  /** Id de proceso; incluye sus subprocesos (so=430). */
  area: string | null;
  page: number;
}

/** Filtros de "+ Filtro" (so=430) y su parámetro en la URL. */
export type ExtraFilter = 'norm' | 'clause' | 'type' | 'area';
export const PARAM: Readonly<Record<ExtraFilter, string>> = {
  norm: 'norma',
  clause: 'clausula',
  type: 'tipo',
  area: 'proceso',
};

/** Id numérico positivo de hasta 18 dígitos (siempre dentro de int8) o null: un id
 *  inválido no llega al API. */
export function idParam(raw: string | null): string | null {
  return raw !== null && /^[1-9]\d{0,17}$/.test(raw) ? raw : null;
}

/** Texto de norma o cláusula recortado, de 1 a 40 caracteres (límites del API) o null. */
export function textParam(raw: string | null): string | null {
  const v = raw?.trim() ?? '';
  return v.length >= 1 && v.length <= 40 ? v : null;
}

export interface QueryParams {
  limit: number;
  page: number;
  q?: string;
  normCode?: string;
  clause?: string;
  documentTypeId?: string;
  processAreaId?: string;
  status?: DocumentStatus;
  overdue?: '1';
  /** "Por firmar" (so=430): con un flujo abierto en aprobación sin firmar. */
  pendingSignature?: '1';
  /** G2-8: "Me toca firmar": lo que le toca firmar al lector. */
  pendingSignatureMine?: '1';
  /** G4: "En capacitación": con la etapa de capacitación abierta. */
  inTraining?: '1';
}

export function parseSegment(raw: string | null): StatusSegment {
  return SEGMENTS.includes(raw as StatusSegment) ? (raw as StatusSegment) : 'todos';
}

export function parsePage(raw: string | null): number {
  const n = Number(raw);
  return Number.isInteger(n) && n >= 1 ? n : 1;
}

/** Traduce los filtros de la vista a los parámetros del endpoint (segmento →
 *  status enum u overdue). Puro y testeable sin router. */
export function toQueryParams(filters: UiFilters, limit: number): QueryParams {
  const q = filters.q.trim();
  const base: QueryParams = {
    limit,
    page: filters.page,
    ...(q ? { q } : {}),
    ...(filters.norm ? { normCode: filters.norm } : {}),
    ...(filters.norm && filters.clause ? { clause: filters.clause } : {}),
    ...(filters.type ? { documentTypeId: filters.type } : {}),
    ...(filters.area ? { processAreaId: filters.area } : {}),
  };
  if (filters.segment === 'vencidos') {
    return { ...base, overdue: '1' };
  }
  if (filters.segment === 'por_firmar') {
    return { ...base, pendingSignature: '1' };
  }
  if (filters.segment === 'mis_firmas') {
    return { ...base, pendingSignatureMine: '1' };
  }
  if (filters.segment === 'en_capacitacion') {
    return { ...base, inTraining: '1' };
  }
  if (filters.segment === 'todos') {
    return base;
  }
  return { ...base, status: filters.segment };
}

/** ¿La vista está acotada por algún criterio? La página no cuenta: distingue "la
 *  empresa no tiene documentos" de "ninguno coincide con los filtros" (QA F1 #9). */
export function isFiltered(filters: UiFilters): boolean {
  return (
    filters.segment !== 'todos' ||
    filters.q.trim() !== '' ||
    filters.norm !== null ||
    filters.type !== null ||
    filters.area !== null
  );
}
